// src/pages/superadmin/order/ViewOrder.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";

const ViewOrder = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const superAdminId = user?.id;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [products, setProducts] = useState([]);

  // edit dialog state
  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    customer: "",
    challanNumber: "",
    date: "",
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        price: 0,
      },
    ],
  });

  // load orders + lookup data
  useEffect(() => {
    (async () => {
      const [oRes, cRes, uRes, pRes, stockRes] = await Promise.all([
        api.get(`/api/orders/get-all/${superAdminId}`),
        api.get(`/api/customers/get-all/${superAdminId}`),
        api.get(`/api/units/get-all/${superAdminId}`),
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/reports/closing-stock/${superAdminId}`),
      ]);
      setOrders(oRes.data);
      setCustomers(cRes.data);
      setUnits(uRes.data);
      // merge closing stock into products
      const stockMap = {};
      stockRes.data.forEach((s) => {
        stockMap[s.productName] = s.closingQty;
      });
      const prods = pRes.data.map((p) => ({
        ...p,
        closingQty: stockMap[p.productName] || 0,
      }));
      setProducts(prods);
    })();
  }, [superAdminId]);

  const fetchOrders = async () => {
    const res = await api.get(`/api/orders/get-all/${superAdminId}`);
    setOrders(res.data);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await api.delete(`/api/orders/delete/${id}`);
    fetchOrders();
  };

  const generatePDF = async (order) => {
    // same iframe PDF logic as before...
    const tplBytes = await fetch("/order-template.pdf").then((r) =>
      r.arrayBuffer()
    );
    const pdfDoc = await PDFDocument.load(tplBytes);
    const page = pdfDoc.getPages()[0];
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const draw = (txt, opts) => {
      try {
        page.drawText(String(txt), opts);
      } catch {}
    };

    draw(order.challanNumber || "N/A", {
      x: 460,
      y: 670,
      size: 12,
      font,
      color: rgb(0, 0, 0),
    });
    draw((order.date || "").slice(0, 10), {
      x: 450,
      y: 700,
      size: 12,
      font,
      color: rgb(0, 0, 0),
    });
    draw(order.customer || "N/A", {
      x: 160,
      y: 675,
      size: 12,
      font,
      color: rgb(0, 0, 0),
    });

    let y0 = 592;
    order.items.slice(0, 15).forEach((it, i) => {
      const y = y0 - 20 * (i + 1);
      draw(it.product || "N/A", { x: 112, y, size: 10, font });
      draw(it.secondaryQuantity || 0, { x: 300, y, size: 10, font });
      draw(it.quantity || 0, { x: 335, y, size: 10, font });
      draw(it.unit || "", { x: 380, y, size: 10, font });
      draw((it.price || 0).toFixed(2), { x: 418, y, size: 10, font });
      draw(((it.quantity || 0) * (it.price || 0)).toFixed(2), {
        x: 480,
        y,
        size: 10,
        font,
      });
    });
    const total = order.items.reduce(
      (s, it) => s + (it.quantity || 0) * (it.price || 0),
      0
    );
    draw(`Total: ₹${total.toFixed(2)}`, { x: 400, y: 140, size: 12, font });

    const bytes = await pdfDoc.save();
    const blob = new Blob([bytes], { type: "application/pdf" });
    setPdfUrl(URL.createObjectURL(blob));
  };

  const handleEdit = (order) => {
    setEditForm({
      _id: order._id,
      customer: order.customer,
      challanNumber: order.challanNumber,
      date: order.date.slice(0, 10),
      items: order.items.map((it) => ({
        product: it.product,
        unit: it.unit,
        availableQty: it.availableQty || 0,
        secondaryQuantity: it.secondaryQuantity ?? 0,
        quantity: it.quantity,
        price: it.price,
        _secFactor: it.secondaryQuantity
          ? it.quantity / it.secondaryQuantity
          : 1,
      })),
    });
    setOpenEdit(true);
  };

  const handleItemChange = (idx, key, val) => {
    const items = [...editForm.items];
    const item = { ...items[idx] };

    if (key === "product") {
      const p = products.find((x) => x.productName === val);
      if (p) {
        item.product = p.productName;
        item.unit = p.primaryUnit;
        item.availableQty = p.closingQty;
        item.price = p.minSalePrice || 0;
        item.secondaryQuantity = 0;
        item.quantity = 0;
        item._secFactor = p.secondaryQuantity || 1;
      }
    } else if (key === "secondaryQuantity") {
      item.secondaryQuantity = val;
      item.quantity = parseFloat((val * (item._secFactor || 1)).toFixed(2));
    } else if (key === "quantity") {
      item.quantity = val;
      item.secondaryQuantity = parseFloat(
        (val / (item._secFactor || 1)).toFixed(2)
      );
    } else if (key === "price") {
      item.price = val;
    }

    items[idx] = item;
    setEditForm((f) => ({ ...f, items }));
  };

  const addRow = () => {
    setEditForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          price: 0,
          _secFactor: 1,
        },
      ],
    }));
  };

  const removeRow = (idx) => {
    setEditForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const submitEdit = async () => {
    const payload = {
      ...editForm,
      superAdminId,
      total: editForm.items.reduce((s, it) => s + it.quantity * it.price, 0),
    };
    await api.put(`/api/orders/update/${editForm._id}`, payload);
    setOpenEdit(false);
    fetchOrders();
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5">View Orders</Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/super-admin/order/create")}
          >
            + Create Order
          </Button>
        </Box>

        {/* Preview Dialog */}
        <Dialog
          open={!!pdfUrl}
          onClose={() => setPdfUrl(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Order Preview</DialogTitle>
          <DialogContent dividers>
            <iframe
              src={pdfUrl}
              title="Order PDF Preview"
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Edit Order</DialogTitle>
          <DialogContent>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                select
                label="Customer"
                value={editForm.customer}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, customer: e.target.value }))
                }
                sx={{ width: 300 }}
              >
                {customers.map((c) => (
                  <MenuItem key={c._id} value={c.partyName}>
                    {c.partyName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Order No"
                value={editForm.challanNumber}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, challanNumber: e.target.value }))
                }
                sx={{ width: 300 }}
              />
              <TextField
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={editForm.date}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, date: e.target.value }))
                }
                sx={{ width: 300 }}
              />
            </Box>

            <Paper>
              <Table size="small">
                <TableBody>
                  {editForm.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          select
                          value={item.product}
                          size="small"
                          onChange={(e) =>
                            handleItemChange(idx, "product", e.target.value)
                          }
                        >
                          {products.map((p) => (
                            <MenuItem key={p._id} value={p.productName}>
                              {p.productName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.unit}
                          size="small"
                          InputProps={{ readOnly: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.availableQty}
                          size="small"
                          label="Avail Stock"
                          InputProps={{ readOnly: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.secondaryQuantity}
                          size="small"
                          label="Secondary Qty"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "secondaryQuantity",
                              Number(e.target.value)
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.quantity}
                          size="small"
                          label="Primary Qty"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "quantity",
                              Number(e.target.value)
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.price}
                          size="small"
                          label="Price"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "price",
                              Number(e.target.value)
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        ₹{(item.quantity * item.price).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => removeRow(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Box mt={1}>
              <Button variant="outlined" onClick={addRow}>
                + Add Row
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={submitEdit}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Orders Table */}
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Actions</TableCell>
                <TableCell>Order No</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => {
                const total = o.items.reduce(
                  (s, it) => s + (it.quantity || 0) * (it.price || 0),
                  0
                );
                return (
                  <TableRow key={o._id}>
                    <TableCell>
                      <IconButton onClick={() => generatePDF(o)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEdit(o)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(o._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{o.challanNumber}</TableCell>
                    <TableCell>{o.customer}</TableCell>
                    <TableCell>
                      {new Date(o.date).toISOString().split("T")[0]}
                    </TableCell>
                    <TableCell>₹{total.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </SuperAdminLayout>
  );
};

export default ViewOrder;
